import React, { useState } from "react";
import { Clock } from "lucide-react";
import { Input, Select, Checkbox, toast } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";
import { DAYS, AFTER_HOURS } from "./constants";
import { Field, StepShell } from "./_ui";
import type { StepProps } from "./types";

interface Row { day_of_week: number; open_time: string; close_time: string; closed: boolean; emergency_service_available: boolean; }

function initRows(hours: Record<string, unknown>[]): Row[] {
  const byDow: Record<number, Record<string, unknown>> = {};
  hours.forEach((h) => { byDow[h.day_of_week as number] = h; });
  return DAYS.map(({ dow }) => {
    const h = byDow[dow];
    if (h) {
      return {
        day_of_week: dow,
        open_time: (h.open_time as string) ?? "08:00",
        close_time: (h.close_time as string) ?? "17:00",
        closed: !!h.closed,
        emergency_service_available: !!h.emergency_service_available,
      };
    }
    const weekend = dow === 0 || dow === 6;
    return { day_of_week: dow, open_time: "08:00", close_time: "17:00", closed: weekend, emergency_service_available: false };
  });
}

export function StepHours({ state, setState, goNext, goBack }: StepProps) {
  const [rows, setRows] = useState<Row[]>(() => initRows(state.hours));
  const [afterHours, setAfterHours] = useState<string>(
    ((state.business_profile as Record<string, string>)?.after_hours_handling) ?? "voicemail",
  );
  const [saving, setSaving] = useState(false);

  const update = (i: number, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const save = async () => {
    setSaving(true);
    try {
      const next = await api.saveHours({ days: rows, after_hours_handling: afterHours });
      setState(next);
      toast.success("Hours saved");
      goNext();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <StepShell
      icon={Clock}
      title="When are you open?"
      description="Your AI uses this to set caller expectations and route after-hours calls."
      onBack={goBack}
      onContinue={save}
      saving={saving}
    >
      <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
        {rows.map((r, i) => (
          <div key={r.day_of_week} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center" data-testid={`hours-row-${r.day_of_week}`}>
            <div className="flex items-center gap-3 sm:w-40">
              <Checkbox checked={!r.closed} onChange={(v) => update(i, { closed: !v })} testid={`hours-open-${r.day_of_week}`} />
              <span className="text-sm font-medium text-slate-800">{DAYS[i].label}</span>
            </div>
            {r.closed ? (
              <span className="text-sm text-slate-400 sm:ml-2">Closed</span>
            ) : (
              <div className="flex items-center gap-2">
                <Input type="time" value={r.open_time} onChange={(e) => update(i, { open_time: e.target.value })}
                  className="w-32" data-testid={`hours-from-${r.day_of_week}`} />
                <span className="text-slate-400">to</span>
                <Input type="time" value={r.close_time} onChange={(e) => update(i, { close_time: e.target.value })}
                  className="w-32" data-testid={`hours-to-${r.day_of_week}`} />
              </div>
            )}
          </div>
        ))}
      </div>
      <Field label="After-hours calls" hint="What should happen when a call comes in outside your hours" testid="field-afterhours">
        <Select value={afterHours} onChange={setAfterHours} options={AFTER_HOURS} testid="onb-afterhours" />
      </Field>
    </StepShell>
  );
}
